// Event handling and shipment submission utilities
import { toastAdd } from "~/utils/globalEvents";
import type { IShipment } from "~/types/shipment";

import { loading, mode, noTransferOpen, open, return_shipment } from "./service";

/**
 * Handles event bus registrations for shipment modal
 */
export const registerShipmentEvents = (
  eventBus: any,
  setSender: () => Promise<void>,
  duplicate: (shipment: IShipment, returnShipment?: boolean) => void
) => {
  return {
    register: () => {
      eventBus.$on("NEWSHIPMENT_CREATED", setSender);

      eventBus.$on("NEWSHIPMENT_TOGGLE", () => {
        noTransferOpen.value = false;
        open.value = !open.value;
        if (open.value) setSender();
        mode.value = "new";
      });

      eventBus.$on("NEWSHIPMENT_OPEN", () => {
        noTransferOpen.value = false;
        open.value = true;
        setSender();
        mode.value = "new";
      });

      eventBus.$on("NEWSHIPMENT_NO_TRANSFER", () => {
        noTransferOpen.value = true;
      });

      eventBus.$on("NEWSHIPMENT_CLOSE", () => {
        open.value = false;
        mode.value = "new";
      });

      eventBus.$on("NEWSHIPMENT_DUPLICATE", (shipment: IShipment) => {
        noTransferOpen.value = false;
        open.value = true;
        mode.value = "duplicate";

        duplicate(shipment);
      });

      eventBus.$on("NEWSHIPMENT_RETURN", (shipment?: IShipment) => {
        mode.value = "return";
        if (shipment) {
          return_shipment.value = shipment;
          duplicate(shipment, true);
        } else setSender();

        open.value = true;
      });
    },
    unregister: () => {
      eventBus.$off("NEWSHIPMENT_TOGGLE");
      eventBus.$off("NEWSHIPMENT_OPEN");
      eventBus.$off("NEWSHIPMENT_CLOSE");
      eventBus.$off("NEWSHIPMENT_DUPLICATE");
      eventBus.$off("NEWSHIPMENT_RETURN");
      eventBus.$off("NEWSHIPMENT_CREATED");
    },
  };
};

/**
 * Handles form submission process
 */
export const submitShipmentForm = async (
  formData: any,
  billingInfo: any,
  prepareShipmentPayload: any,
  formatDate: any,
  homeFetch: any,
  handleSuccessfulShipment: any,
  handlePayment: any
) => {
  loading.value = true;

  // Prepare API request payload
  const data = prepareShipmentPayload(formData, billingInfo, formatDate);

  // Submit the shipment creation request
  try {
    const response = await homeFetch("shipments", {
      method: "POST",
      body: data,
    });

    const orderId = response.data?.order?.id ?? null;

    if (response.data.processed) {
      handleSuccessfulShipment(response.data.shipment);
      return null;
    } else {
      handlePayment(response.data, orderId);
      return orderId;
    }
  } catch (error) {
    loading.value = false;
    return null;
  }
};

/**
 * Handles successful shipment creation
 */
export const handleSuccessfulShipment = (
  shipment: any,
  t: any,
  eventBus: any,
  currentStepIdx: any,
  setShipmentCreated: (value: boolean) => void,
  setPackageInfo: (number: string | null, id: string | null) => void
) => {
  loading.value = false;
  setShipmentCreated(true);
  setPackageInfo(shipment.package_number, shipment.id);

  eventBus.$emit("NEWSHIPMENT_CREATED");
  currentStepIdx.value = 0;
  return_shipment.value = null;

  toastAdd({
    group: "shipment_created",
    text: t("shipment_created_with_id", { id: shipment.package_number }),
  });
};

/**
 * Processes payment for shipment
 */
export const processOrder = async (orderId: string | number | null, homeFetch: any, handleSuccessfulShipment: any) => {
  if (!orderId) return;

  try {
    const response = await homeFetch(`shipments/${orderId}/process`, {
      method: "POST",
    });

    handleSuccessfulShipment(response.data.shipment);
  } catch (error) {
    loading.value = false;
  }
};

/**
 * Handles the payment window event handler
 */
export const createPaymentEventHandler = (orderId: string | number | null, processOrder: any) => {
  return function eventHandler(event: MessageEvent) {
    if (event.data === "payment:success") {
      processOrder(orderId);
      window.removeEventListener("message", eventHandler);
    }
  };
};
